import React, { useEffect, useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Image,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import { ProcedureChecklistFormData, ChecklistItemFormData, ChecklistSubItemFormData } from '@/types/ProcedureChecklist';
import { Category, DEFAULT_CATEGORY_ID } from '@/types/Category';
import { CategoryService } from '@/services/categoryService';
import { useAuth } from '@/contexts/AuthContext';
import { useCrossPlatformAlert } from '@/components/CrossPlatformAlert';
import { useOfflineButtons } from '@/utils/useOfflineButtons';
import { useLocalSearchParams } from 'expo-router';
import { useResponsiveLayout } from '@/utils/useResponsiveLayout';
import SubItemForm from '@/components/SubItemForm';

interface ProcedureFormProps {
  mode: 'create' | 'edit';
  initialData?: ProcedureChecklistFormData;
  onSave: (data: ProcedureChecklistFormData) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

export default function ProcedureForm({ mode, initialData, onSave, onCancel, loading = false }: ProcedureFormProps) {
  const { t } = useTranslation('common');
  const { isButtonDisabled, getDisabledStyle } = useOfflineButtons();
  const crossPlatformAlert = useCrossPlatformAlert();
  const { user } = useAuth();
  const params = useLocalSearchParams();
  const responsive = useResponsiveLayout();

  // State for categories
  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  // Default form data
  const defaultFormData: ProcedureChecklistFormData = {
    title: '',
    description: '',
    items: [],
    categories: [],
  };

  const [formData, setFormData] = useState<ProcedureChecklistFormData>(initialData || defaultFormData);

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    if (!user) return;
    
    try {
      const categoriesData = await CategoryService.getCategories(user.role);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Don't show alert here, just use default category
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  }, [user]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    } else if (mode === 'create') {
      let defaultCategories = [];
      
      // If categoryId is passed as parameter, use it
      if (params.categoryId) {
        defaultCategories = [params.categoryId as string];
      } else {
        defaultCategories = [DEFAULT_CATEGORY_ID];
      }
      
      setFormData({ 
        ...defaultFormData, 
        categories: defaultCategories 
      });
    }
  }, [initialData, mode, params.categoryId]);

  const updateFormData = (field: keyof ProcedureChecklistFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateItemFormData = (index: number, field: keyof ChecklistItemFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const updateItemSubItems = (itemIndex: number, subItems: ChecklistSubItemFormData[]) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === itemIndex ? { ...item, subItems } : item
      ),
    }));
  };

  const addSubItemToItem = (itemIndex: number) => {
    const newSubItem: ChecklistSubItemFormData = {
      id: `sub-${Date.now()}`,
      topic: '',
      type: 'simple',
    };
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === itemIndex
          ? { ...item, subItems: [...(item.subItems || []), newSubItem] }
          : item
      ),
    }));
  };

  const addItem = () => {
    const newItem: ChecklistItemFormData = {
      id: `new-${Date.now()}`,
      topic: '',
      content: '',
      number: formData.items.length + 1,
      link: '',
    };
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
  };

  const removeItem = (index: number) => {
    crossPlatformAlert.showAlert({
      title: t('procedureForm.confirmDelete'),
      message: t('procedureForm.confirmDeleteMessage'),
      buttons: [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('common.delete'),
          style: 'destructive',
          onPress: () => {
            setFormData(prev => ({
              ...prev,
              items: prev.items.filter((_, i) => i !== index).map((item, i) => ({
                ...item,
                number: i + 1
              }))
            }));
          }
        }
      ]
    });
  };

  const moveItem = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= formData.items.length) return;

    setFormData(prev => {
      const newItems = [...prev.items];
      [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
      
      // Update numbers
      return {
        ...prev,
        items: newItems.map((item, i) => ({ ...item, number: i + 1 }))
      };
    });
  };

  const pickImage = async (itemId: string) => {
    try {
      // Request permission
      const permissionResult = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (!permissionResult.granted) {
        crossPlatformAlert.showAlert({ 
          title: t('procedureForm.permissionRequired'), 
          message: t('procedureForm.permissionDenied') 
        });
        return;
      }

      // Launch image picker
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ['images'],
        allowsEditing: false,
        quality: 0.8,
      });

      if (!result.canceled && result.assets[0]) {
        const itemIndex = formData.items.findIndex(item => item.id === itemId);
        if (itemIndex !== -1) {
          // Use the original image URI directly - no processing or blob conversion for form display
          const originalUri = result.assets[0].uri;
          console.log(`[ProcedureForm] New image picked for item ${itemId}: ${originalUri.substring(0, 50)}...`);
          
          updateItemFormData(itemIndex, 'image', originalUri);
        }
      }
    } catch (error) {
      console.error('Error picking image:', error);
      crossPlatformAlert.showAlert({ 
        title: t('procedureForm.error'), 
        message: t('procedureForm.failedToPickImage') 
      });
    }
  };

  const validateForm = (): boolean => {
    if (!(formData.title || '').trim()) {
      crossPlatformAlert.showAlert({ 
        title: t('procedureForm.error'), 
        message: t('procedureForm.titleRequired') 
      });
      return false;
    }

    if (formData.items.length === 0) {
      crossPlatformAlert.showAlert({ 
        title: t('procedureForm.error'), 
        message: t('procedureForm.itemsRequired') 
      });
      return false;
    }

    for (let i = 0; i < formData.items.length; i++) {
      const item = formData.items[i];
      if (!(item.topic || '').trim()) {
        crossPlatformAlert.showAlert({ 
          title: t('procedureForm.error'), 
          message: t('procedureForm.itemTopicRequired', { number: i + 1 }) 
        });
        return false;
      }
      // Content is required unless the item has nested sub-items (which serve as an alternative to flat content)
      const hasSubItems = item.subItems && item.subItems.length > 0;
      if (!hasSubItems && !(item.content || '').trim()) {
        crossPlatformAlert.showAlert({ 
          title: t('procedureForm.error'), 
          message: t('procedureForm.itemContentRequired', { number: i + 1 }) 
        });
        return false;
      }
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm() || isButtonDisabled()) return;

    try {
      // Form data is already clean - no need for sanitization since we're not using caching
      console.log('[ProcedureForm] Saving form data', formData);
      await onSave(formData);
    } catch (error) {
      // Error handling is done by the parent component
      throw error;
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView 
        contentContainerStyle={[
          styles.scrollContent,
          responsive.isDesktop && {
            paddingHorizontal: responsive.spacing.large,
            alignItems: 'center',
          }
        ]}
      >
        <View style={[
          styles.card,
          responsive.isDesktop && {
            maxWidth: responsive.maxFormWidth,
            width: '100%',
          }
        ]}>
          <Text style={[
            styles.title,
            { fontSize: responsive.fontSize.title }
          ]}>
            {mode === 'create' ? t('procedureForm.create') : t('procedureForm.update')}
          </Text>

          <View style={styles.section}>
            <Text style={[
              styles.sectionTitle,
              { fontSize: responsive.fontSize.subtitle }
            ]}>{t('procedureForm.header')}</Text>
            
            <Text style={styles.label}>{t('procedureForm.title')} *</Text>
            <TextInput
              style={styles.input}
              value={formData.title}
              onChangeText={(value) => updateFormData('title', value)}
              placeholder={t('procedureForm.titlePlaceholder')}
            />

            <Text style={styles.label}>{t('procedureForm.description')}</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.description}
              onChangeText={(value) => updateFormData('description', value)}
              placeholder={t('procedureForm.descriptionPlaceholder')}
              multiline
              numberOfLines={3}
            />

            {/* Category Selection */}
            <Text style={styles.label}>{t('procedureForm.categories')}</Text>
            {loadingCategories ? (
              <View style={styles.categoriesLoading}>
                <ActivityIndicator size="small" color="#0066CC" />
                <Text style={styles.categoriesLoadingText}>{t('categories.loading')}</Text>
              </View>
            ) : (
              <View style={styles.categoriesContainer}>
                {categories.length === 0 ? (
                  <Text style={styles.noCategoriesText}>{t('categories.empty.noCategories')}</Text>
                ) : (
                  categories.map((category) => {
                    const isSelected = formData.categories?.includes(category.id) || false;
                    return (
                      <TouchableOpacity
                        key={category.id}
                        style={[
                          styles.categoryOption,
                          isSelected && styles.categoryOptionSelected
                        ]}
                        onPress={() => {
                          const currentCategories = formData.categories || [];
                          let newCategories;
                          
                          if (isSelected) {
                            // Remove category
                            newCategories = currentCategories.filter(id => id !== category.id);
                            // If no categories left, add default category
                            if (newCategories.length === 0) {
                              newCategories = [DEFAULT_CATEGORY_ID];
                            }
                          } else {
                            // Add category and remove default if it was the only one
                            if (currentCategories.length === 1 && currentCategories[0] === DEFAULT_CATEGORY_ID) {
                              newCategories = [category.id];
                            } else {
                              newCategories = [...currentCategories.filter(id => id !== DEFAULT_CATEGORY_ID), category.id];
                            }
                          }
                          
                          updateFormData('categories', newCategories);
                        }}
                      >
                        <View style={styles.categoryOptionContent}>
                          {category.color && (
                            <View style={[styles.categoryColorDot, { backgroundColor: category.color }]} />
                          )}
                          <Text style={[
                            styles.categoryOptionText,
                            isSelected && styles.categoryOptionTextSelected
                          ]}>
                            {category.name}
                          </Text>
                          {isSelected && (
                            <Ionicons name="checkmark-circle" size={20} color="#0066CC" />
                          )}
                        </View>
                        {category.description && (
                          <Text style={[
                            styles.categoryOptionDescription,
                            isSelected && styles.categoryOptionDescriptionSelected
                          ]}>
                            {category.description}
                          </Text>
                        )}
                      </TouchableOpacity>
                    );
                  })
                )}
              </View>
            )}
          </View>

          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>{t('procedureForm.checklistItems')}</Text>
              <TouchableOpacity style={styles.addButton} onPress={addItem}>
                <Ionicons name="add-circle" size={24} color="#0066CC" />
                <Text style={styles.addButtonText}>{t('procedureForm.addItem')}</Text>
              </TouchableOpacity>
            </View>

            {formData.items.map((item, index) => (
              <View key={item.id} style={styles.itemContainer}>
                <View style={styles.itemHeader}>
                  <Text style={styles.itemNumber}>{index + 1}</Text>
                  <View style={styles.itemControls}>
                    {index > 0 && (
                      <TouchableOpacity onPress={() => moveItem(index, 'up')}>
                        <Ionicons name="arrow-up" size={20} color="#666" />
                      </TouchableOpacity>
                    )}
                    {index < formData.items.length - 1 && (
                      <TouchableOpacity onPress={() => moveItem(index, 'down')}>
                        <Ionicons name="arrow-down" size={20} color="#666" />
                      </TouchableOpacity>
                    )}
                    <TouchableOpacity onPress={() => removeItem(index)}>
                      <Ionicons name="trash" size={20} color="#ff0000" />
                    </TouchableOpacity>
                  </View>
                </View>

                <Text style={styles.label}>{t('procedureForm.topic')} *</Text>
                <TextInput
                  style={styles.input}
                  value={item.topic}
                  onChangeText={(value) => updateItemFormData(index, 'topic', value)}
                  placeholder={t('procedureForm.topicPlaceholder')}
                />

                {/* Flat content – only shown when item has no sub-items */}
                {!(item.subItems && item.subItems.length > 0) && (
                  <>
                    <Text style={styles.label}>{t('procedureForm.content')} *</Text>
                    <TextInput
                      style={[styles.input, styles.textArea]}
                      value={item.content}
                      onChangeText={(value) => updateItemFormData(index, 'content', value)}
                      placeholder={t('procedureForm.contentPlaceholder')}
                      multiline
                      numberOfLines={4}
                    />
                  </>
                )}

                <Text style={styles.label}>{t('procedureForm.image')}</Text>
                {item.image ? (
                  <View style={styles.imageContainer}>
                    <Image 
                      source={{ uri: item.image }} 
                      style={styles.previewImage} 
                    />
                    <TouchableOpacity
                      style={styles.changeImageButton}
                      onPress={() => pickImage(item.id)}
                    >
                      <Text style={styles.changeImageText}>{t('procedureForm.changeImage')}</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.removeImageButton}
                      onPress={() => {
                        updateItemFormData(index, 'image', undefined);
                      }}
                    >
                      <Text style={styles.removeImageText}>{t('procedureForm.removeImage')}</Text>
                    </TouchableOpacity>
                  </View>
                ) : (
                  <TouchableOpacity
                    style={styles.addImageButton}
                    onPress={() => pickImage(item.id)}
                  >
                    <Ionicons name="image-outline" size={24} color="#0066CC" />
                    <Text style={styles.addImageText}>{t('procedureForm.addImage')}</Text>
                  </TouchableOpacity>
                )}

                <Text style={styles.label}>{t('procedureForm.link')}</Text>
                <TextInput
                  style={styles.input}
                  value={item.link || ''}
                  onChangeText={(value) => updateItemFormData(index, 'link', value)}
                  placeholder={t('procedureForm.linkPlaceholder')}
                />

                {/* Sub-items section */}
                <View style={styles.subItemsSectionHeader}>
                  <Text style={styles.subItemsSectionTitle}>{t('procedureForm.subItems.title')}</Text>
                  <TouchableOpacity
                    style={styles.addSubItemButton}
                    onPress={() => addSubItemToItem(index)}
                  >
                    <Ionicons name="add-circle-outline" size={20} color="#0066CC" />
                    <Text style={styles.addSubItemText}>{t('procedureForm.subItems.add')}</Text>
                  </TouchableOpacity>
                </View>

                {item.subItems && item.subItems.length > 0 && (
                  <View style={styles.subItemsList}>
                    {item.subItems.map((subItem, subIdx) => (
                      <SubItemForm
                        key={subItem.id}
                        subItem={subItem}
                        depth={0}
                        onUpdate={(updated) => {
                          const newSubItems = [...(item.subItems || [])];
                          newSubItems[subIdx] = updated;
                          updateItemSubItems(index, newSubItems);
                        }}
                        onRemove={() => {
                          const newSubItems = (item.subItems || []).filter((_, i) => i !== subIdx);
                          updateItemSubItems(index, newSubItems);
                        }}
                      />
                    ))}
                  </View>
                )}
              </View>
            ))}

            {formData.items.length === 0 && (
              <View style={styles.emptyState}>
                <Text style={styles.emptyStateText}>{t('procedureForm.noItemsYet')}</Text>
                <TouchableOpacity style={styles.addFirstButton} onPress={addItem}>
                  <Text style={styles.addFirstButtonText}>{t('procedureForm.clickAddItem')}</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>

          <View style={styles.actionButtons}>
            <TouchableOpacity
              style={styles.cancelButton}
              onPress={onCancel}
              disabled={loading}
            >
              <Text style={styles.cancelButtonText}>{t('common.cancel')}</Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={[styles.submitButton, (loading || isButtonDisabled()) && styles.disabledButton, getDisabledStyle()]}
              onPress={handleSave}
              disabled={loading || isButtonDisabled()}
            >
              {loading ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <Text style={styles.submitButtonText}>
                  {mode === 'create' ? t('procedureForm.create') : t('procedureForm.update')}
                </Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  scrollContent: {
    padding: 16,
  },
  card: {
    padding: 20,
    backgroundColor: '#fff',
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3.84,
    elevation: 5,
  },
  title: {
    fontWeight: 'bold',
    marginBottom: 20,
    textAlign: 'center',
    color: '#333',
  },
  section: {
    marginBottom: 20,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  sectionTitle: {
    fontWeight: 'bold',
    color: '#333',
  },
  label: {
    fontSize: 16,
    fontWeight: '500',
    marginBottom: 6,
    color: '#333',
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 6,
    padding: 12,
    fontSize: 16,
    backgroundColor: '#fff',
    marginBottom: 16,
  },
  textArea: {
    height: 80,
    textAlignVertical: 'top',
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  addButtonText: {
    color: '#0066CC',
    fontWeight: '500',
  },
  itemContainer: {
    backgroundColor: '#f8f9fa',
    padding: 16,
    borderRadius: 8,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#e9ecef',
  },
  itemHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  itemNumber: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#0066CC',
  },
  itemControls: {
    flexDirection: 'row',
    gap: 12,
  },
  emptyState: {
    alignItems: 'center',
    padding: 32,
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e9ecef',
    borderStyle: 'dashed',
  },
  emptyStateText: {
    fontSize: 16,
    color: '#666',
    marginBottom: 16,
  },
  addFirstButton: {
    backgroundColor: '#0066CC',
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 6,
  },
  addFirstButtonText: {
    color: '#fff',
    fontWeight: '500',
  },
  actionButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 20,
    gap: 12,
  },
  submitButton: {
    backgroundColor: '#0066CC',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 6,
    flex: 1,
    alignItems: 'center',
  },
  submitButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
  cancelButton: {
    backgroundColor: '#666',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 6,
    flex: 1,
    alignItems: 'center',
  },
  cancelButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
  disabledButton: {
    backgroundColor: '#ccc',
  },
  imageContainer: {
    marginBottom: 16,
  },
  previewImage: {
    width: '100%',
    height: 200,
    borderRadius: 8,
    resizeMode: 'cover',
    marginBottom: 8,
  },
  changeImageButton: {
    backgroundColor: '#4CAF50',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
    marginBottom: 8,
  },
  changeImageText: {
    color: '#fff',
    fontWeight: '600',
    textAlign: 'center',
  },
  removeImageButton: {
    backgroundColor: '#F44336',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
  },
  removeImageText: {
    color: '#fff',
    fontWeight: '600',
    textAlign: 'center',
  },
  addImageButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f0f0f0',
    paddingVertical: 40,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: '#e0e0e0',
    borderStyle: 'dashed',
    marginBottom: 16,
  },
  addImageText: {
    color: '#0066CC',
    fontSize: 16,
    fontWeight: '600',
    marginLeft: 8,
  },
  // Sub-items section styles
  subItemsSectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 16,
    marginBottom: 8,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
  },
  subItemsSectionTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: '#333',
  },
  addSubItemButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  addSubItemText: {
    color: '#0066CC',
    fontSize: 14,
    fontWeight: '500',
  },
  subItemsList: {
    marginBottom: 4,
  },
  // Category selection styles
  categoriesLoading: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    backgroundColor: '#f9f9f9',
    borderRadius: 8,
    marginBottom: 8,
  },
  categoriesLoadingText: {
    marginLeft: 8,
    color: '#666',
  },
  categoriesContainer: {
    marginBottom: 8,
  },
  noCategoriesText: {
    color: '#999',
    fontStyle: 'italic',
    padding: 16,
    textAlign: 'center',
    backgroundColor: '#f9f9f9',
    borderRadius: 8,
  },
  categoryOption: {
    backgroundColor: '#f9f9f9',
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  categoryOptionSelected: {
    backgroundColor: '#e3f2fd',
    borderColor: '#0066CC',
  },
  categoryOptionContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  categoryColorDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginRight: 8,
  },
  categoryOptionText: {
    fontSize: 16,
    fontWeight: '500',
    color: '#333',
    flex: 1,
  },
  categoryOptionTextSelected: {
    color: '#0066CC',
  },
  categoryOptionDescription: {
    fontSize: 12,
    color: '#666',
    marginTop: 4,
    marginLeft: 20,
  },
  categoryOptionDescriptionSelected: {
    color: '#0066CC',
  },
});
